<template>
  <div
    class="min-h-screen container mx-auto px-4 py-32 flex items-center justify-center relative overflow-hidden"
  >
    <button
      @click="prev"
      :class="[
        'absolute left-5 z-50 p-4 rounded-full bg-white/80 shadow-lg hover:bg-white transition-all',
        currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
      ]"
      :disabled="currentIndex === 0"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </button>

    <div class="w-full overflow-hidden px-4">
      <div
        class="flex transition-transform duration-500 ease-in-out"
        :style="{ transform: `translateX(-${currentIndex * 240}px)` }"
      >
        <div
          v-for="(item, index) in timelineData"
          :key="index"
          class="relative flex-shrink-0 h-[700px] flex flex-col w-[240px] bg-white shadow-xl"
        >
          <div
            class="absolute inset-0 pointer-events-none"
            :class="
              index % 2 === 0
                ? 'bg-gradient-to-r from-white to-gray-50'
                : 'bg-gradient-to-r from-gray-50 to-gray-100'
            "
          ></div>

          <div
            class="flex-1 z-10 flex flex-col items-center justify-center p-6 text-center"
          >
            <template v-if="index % 2 !== 0">
              <h3 class="font-black text-lg uppercase text-gray-800 mb-2">
                {{ item.title }}
              </h3>
              <p
                class="text-[12px] text-gray-500 leading-relaxed mb-4 line-clamp-3"
              >
                {{ item.desc }}
              </p>
              <div
                class="w-[200px] h-32 rounded custom-stripe cursor-zoom-in"
                @click="openImage(item.img)"
              >
                <img :src="item.img" class="w-full h-full object-cover" />
              </div>
            </template>
          </div>

          <div
            class="h-[60px] flex items-center justify-center text-white text-xl font-black relative z-20"
            :class="[
              item.colorClass,
              index % 2 === 0 ? 'arrow-down' : 'arrow-up'
            ]"
          >
            <div
              class="absolute inset-0 bg-gradient-to-r from-white/10 to-black/10 pointer-events-none"
            ></div>
            {{ item.year }}
          </div>

          <div
            class="flex-1 z-10 flex flex-col items-center justify-center p-6 text-center"
          >
            <template v-if="index % 2 === 0">
              <div
                class="w-[200px] h-32 rounded custom-stripe mb-4 cursor-zoom-in"
                @click="openImage(item.img)"
              >
                <img :src="item.img" class="w-full object-cover" />
              </div>
              <h3 class="font-black text-lg uppercase text-gray-800 mt-2">
                {{ item.title }}
              </h3>
              <p class="text-[12px] text-gray-500 leading-relaxed line-clamp-3">
                {{ item.desc }}
              </p>
            </template>
          </div>
        </div>
      </div>
    </div>

    <button
      @click="next"
      :class="[
        'absolute right-5 z-50 p-4 rounded-full bg-white/80 shadow-lg hover:bg-white transition-all',
        isEnd ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
      ]"
      :disabled="isEnd"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>

    <transition name="fade">
      <div
        v-if="previewUrl"
        class="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-10"
        @click="previewUrl = null"
      >
        <img
          :src="previewUrl"
          class="max-w-full max-h-full shadow-2xl rounded-lg animate-scaleIn"
          alt="preview"
        />
        <button
          class="absolute top-10 right-10 text-white hover:rotate-90 transition-transform"
        >
          <svg
            class="w-10 h-10"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      currentIndex: 0,
      visibleSteps: 5, // 视口内同时显示的卡片数量
      previewUrl: null, // 当前查看的大图地址
      timelineData: [
        {
          year: '2006',
          title: 'Start Up',
          desc: 'The beginning of our journey in the industry.',
          colorClass: 'bg-[#d11252]',
          img: require('@/assets/h-01.jpg')
        },
        {
          year: '2010',
          title: 'Innovation',
          desc: 'Introducing new technologies and tools.',
          colorClass: 'bg-[#e31e24]',
          img: require('@/assets/h-02.jpg')
        },
        {
          year: '2019',
          title: 'Expansion',
          desc: 'Moving to new territories and markets.',
          colorClass: 'bg-[#f36f21]',
          img: require('@/assets/h-03.jpg')
        },
        {
          year: '2023',
          title: 'Global',
          desc: 'Establishing a presence worldwide.',
          colorClass: 'bg-[#f99d1c]',
          img: require('@/assets/h-04.png')
        },
        {
          year: '2024',
          title: 'Explore',
          desc: 'Discovering new possibilities and pathways.',
          colorClass: 'bg-[#ffcc00]',
          img: require('@/assets/h-05.jpg')
        },
        {
          year: '2026',
          title: 'Growth',
          desc: 'Continuous growth.',
          colorClass: 'bg-[#8cc63f]',
          img: require('@/assets/h-06.png')
        },
        {
          year: '2027',
          title: 'Modern',
          desc: 'Adopting AI and cloud technologies.',
          colorClass: 'bg-[#00a651]',
          img: require('@/assets/h-07.jpg')
        },
        {
          year: '2030',
          title: 'Future',
          desc: 'Looking ahead to sustainable solutions.',
          colorClass: 'bg-[#00aeef]',
          img: require('@/assets/h-08.jpg')
        }
      ]
    }
  },
  computed: {
    isEnd() {
      // 当剩余卡片不足以填满一个视口时禁用
      return this.currentIndex >= this.timelineData.length - this.visibleSteps
    }
  },
  methods: {
    next() {
      if (!this.isEnd) {
        this.currentIndex++
      }
    },
    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
    },
    openImage(url) {
      this.previewUrl = url
    }
  }
}
</script>

<style scoped>
.arrow-down::after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 15px;
  background-color: inherit;
  clip-path: polygon(0 0, 100% 0, 50% 100%);
}
.arrow-up::after {
  content: '';
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 15px;
  background-color: inherit;
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
}
</style>



// WEBPACK FOOTER //
// src/page/aboutus/OurHistory.vue